import type { FetchArgs } from "@reduxjs/toolkit/query"

const SUBMISSION_EMAIL_OVERRIDE_EXCLUDED_PATHS = [
  "/jwt/",
  "/idp/",
  "/member-id",
  "/logout",
  "/yodlee/",
] as const

function isSubmissionEmailOverrideEnvironment(): boolean {
  if (typeof window === "undefined") {
    return false
  }
  const host = window.location.hostname
  return (
    /^(localhost|(?:[0-9]{1,3}\.){3}[0-9]{1,3}|local)/.test(host) ||
    host === "mol-uat.gesb.wa.gov.au"
  )
}

/** Read `?email=` from pre-hash search, then hash query (HashRouter). */
export function readSubmissionEmailFromLocation(
  search = typeof window !== "undefined" ? window.location.search : "",
  hash = typeof window !== "undefined" ? window.location.hash : ""
): string | undefined {
  const fromSearch = new URLSearchParams(search).get("email")?.trim()
  if (fromSearch) {
    return fromSearch
  }

  const queryStart = hash.indexOf("?")
  if (queryStart >= 0) {
    const fromHash = new URLSearchParams(hash.slice(queryStart))
      .get("email")
      ?.trim()
    if (fromHash) {
      return fromHash
    }
  }

  return undefined
}

/** Local/UAT only — optional confirmation-mail recipient from `?email=`. */
export function getSubmissionEmailOverride(): string | undefined {
  if (typeof window === "undefined") {
    return undefined
  }
  if (!isSubmissionEmailOverrideEnvironment()) {
    return undefined
  }
  return readSubmissionEmailFromLocation()
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value)
}

export function withSubmissionEmailOverride<T extends Record<string, unknown>>(
  body: T,
  emailOverride?: string
): T {
  const email = emailOverride ?? getSubmissionEmailOverride()
  if (!email) {
    return body
  }

  if (isPlainObject(body.squiz)) {
    if (body.squiz.email) {
      return body
    }
    return {
      ...body,
      squiz: { ...body.squiz, email },
    }
  }

  if ("email" in body && body.email) {
    return body
  }
  return { ...body, email }
}

function shouldApplySubmissionEmailOverride(url: string | undefined): boolean {
  if (!url) {
    return false
  }
  return !SUBMISSION_EMAIL_OVERRIDE_EXCLUDED_PATHS.some((path) =>
    url.includes(path)
  )
}

function isJsonSubmissionBody(body: unknown): body is Record<string, unknown> {
  return (
    body != null &&
    typeof body === "object" &&
    !Array.isArray(body) &&
    !(body instanceof FormData)
  )
}

/** Apply UAT/local `?email=` to RTK fetch args before submission. */
export function applySubmissionEmailOverrideToFetchArgs(
  args: string | FetchArgs
): string | FetchArgs {
  if (typeof args === "string" || !getSubmissionEmailOverride()) {
    return args
  }

  const method = (args.method ?? "GET").toUpperCase()
  if (method !== "POST" && method !== "PUT") {
    return args
  }

  if (!shouldApplySubmissionEmailOverride(args.url)) {
    return args
  }

  if (!isJsonSubmissionBody(args.body)) {
    return args
  }

  return {
    ...args,
    body: withSubmissionEmailOverride(args.body),
  }
}
